
const libPictProvider = require('pict-provider');
const libBuilder = require('../datalake/Datalake-Projection-Builder.js');
const libSourceEntities = require('../datalake/Datalake-Source-Entities.js');
const libCatalog = require('./DatalakeCatalog-Provider.js');

/**
 * Create-projection wizard provider.
 *
 * A 5-step flow that builds a materialized Extraction projection from the Headlight data model and submits
 * it to POST /1.0/Projection: (1) pick a source entity + optional FoxHound filter (+ a "test source" row
 * count); (2) pick the output columns from the entity's live /Schema + the GUID template; (3) name +
 * auto-derived silo Target + optional refresh schedule; (4) review the generated POST body; (5) submit +
 * show the run result, then apply the schedule via a follow-up PUT (POST /1.0/Projection takes no schedule
 * fields). All decisions go through the pure Datalake-Projection-Builder so they're unit-tested; AppData
 * holds data + slots only.
 *
 * Tier 1 (this provider) extracts structural meadow columns. Tier 2 (Document + a DocumentType → FormData
 * fields from the ArtifactType schema) layers onto the same flow via the FormData hooks.
 */

const AUDIT_FIELDS = { CreateDate: 1, CreatingIDUser: 1, UpdateDate: 1, UpdatingIDUser: 1, Deleted: 1, DeleteDate: 1, DeletingIDUser: 1 };

/** Audit-ish columns excluded from the default selection (the lake adds its own audit + GUID columns). */
function isAuditField(pName, pEntity)
{
	return !!AUDIT_FIELDS[pName] || (pName === `GUID${pEntity}`);
}

const STEP_COUNT = 5;

class DatalakeProjectionEditorProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._schemaCache = {};
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	render()
	{
		if (this.pict.views.DatalakeProjectionEditor)
		{
			this.pict.views.DatalakeProjectionEditor.render();
		}
	}

	get isSuperUser()
	{
		const tmpUser = this.pict.AppData.CurrentUser || {};
		return (parseInt(tmpUser.UserRoleIndex, 10) === 5) && (parseInt(tmpUser.CustomerID, 10) === 1);
	}

	initializeState()
	{
		const tmpCustomerID = parseInt((this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0, 10);
		this.pict.AppData.DatalakeProjectionEditor =
		{
			ActiveStep: 1,
			CompletedSteps: [],
			Mode: 'extraction', // 'extraction' | 'clone' (projectionize a raw imported lake table)
			LakeSource: '',
			LakeSourceTable: '',
			Step1_CardClass: '',
			Step2_CardClass: '',
			CloneBannerSlot: [],

			// Step 1 — Source
			SourceType: 'entity', // 'entity' (Headlight model) | 'lake' (existing projection / imported clone)
			Entity: '',
			EntityLabel: '',
			IsFormDataEntity: false,
			DocumentType: '',
			Filter: '',
			SourceConnectionPrefix: 'headlight_api',
			SourceEntityOptions: [],
			LakeKind: '',         // when SourceType==='lake': 'computed' | 'imported'
			LakeSourceLabel: '',
			LakeSourceValue: '',  // the picker's composite value: `${Kind}::${Physical}` (or '' for none)
			LakeSourceGroups: [], // [{ GroupLabel, Items:[{ Physical, Label, Kind, SelectedAttr }] }]
			LakeSourcesLoaded: false,
			LakeSourcesLoadingSlot: [],
			TestResultSlot: [],
			TestBusy: false,

			// Step 2 — Columns + GUID
			SchemaLoadingSlot: [],
			SchemaErrorSlot: [],
			ShowAll: false,
			Fields: [],          // working model: { SourceField, OutCol, JsonType, Size, Include, IsAudit }
			FieldRows: [],       // derived render rows
			FormDataFields: [],  // Tier 2: manually-added FormData.<key> columns ({ Key, OutCol })
			FormDataRows: [],    // derived render rows for FormData
			IncludedCount: 0,
			GUIDTemplate: '',

			// Step 3 — Name / Target / Schedule
			IDCustomer: tmpCustomerID,
			IDCustomerEditable: this.isSuperUser,
			Name: '',
			Description: '',
			TargetPreview: '',
			NameErrorSlot: [],
			ScheduleEnabled: false,
			ScheduleMode: 'interval',
			Cron: '',
			IntervalMinutes: 1440,
			Group: '',
			ScheduleEnabledAttr: '',
			ScheduleModeIntervalChecked: 'checked',
			ScheduleModeCronChecked: '',
			ScheduleFieldsClass: 'is-hidden',
			ScheduleIntervalClass: '',
			ScheduleCronClass: 'is-hidden',

			// Step 4 — Review
			ReviewJSON: '',
			ValidationSlot: [],

			// Step 5 — Submit + result
			Submitting: false,
			SubmitBusySlot: [],
			ResultSlot: [],
			NewDatasetGUID: '',
		};
	}

	/** Entry point from the route: reset + seed the source-entity picker. */
	newProjection()
	{
		this.initializeState();
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.SourceEntityOptions = libSourceEntities.DATALAKE_SOURCE_ENTITIES.map((pEntity) => ({
			Entity: pEntity.Entity,
			Label: pEntity.Label,
			SelectedAttr: '',
		}));
		this.render();
	}

	/**
	 * Open the wizard in "clone" mode pre-filled to projectionize a raw imported lake table: a 1:1
	 * PassthroughClone of the table into a new, registered (queryable) lake table. Source + columns are
	 * implicit, so it jumps to naming.
	 * @param {string} pPhysicalTableName
	 */
	newFromLakeTable(pPhysicalTableName)
	{
		this.newProjection();
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.Mode = 'clone';
		tmpState.LakeSourceTable = pPhysicalTableName;
		tmpState.LakeSource = `lake/${pPhysicalTableName}`;
		tmpState.CompletedSteps = [ 1, 2 ];
		tmpState.ActiveStep = 3;
		// Suggested name (must differ from the source table) — the logical name + a "_q" (queryable) suffix.
		const tmpLogical = pPhysicalTableName.replace(/^C\d+_/, '');
		tmpState.Name = `${tmpLogical}_q`.replace(/[^A-Za-z0-9_]/g, '_').slice(0, 110);
		// PassthroughClone needs a per-row key; imported lake tables carry a synthetic 'GUID' column
		// (private-lake-ingest's Schema-Inferrer always prepends an AutoIdentity 'ID' + AutoGUID 'GUID').
		tmpState.GUIDTemplate = '{~D:Record.GUID~}';
		this.deriveRenderState();
		this.render();
	}

	// --- derived render state ---

	deriveRenderState()
	{
		if (!this.pict.AppData.DatalakeProjectionEditor)
		{
			this.newProjection();
		}
		this.deriveStepState();
		this.deriveFieldState();
		this.deriveFormDataRows();
		this.deriveNameState();
		this.deriveScheduleAttrs();

		// Clone (projectionize) mode: hide the source + columns steps, show a "cloning X" banner.
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		const tmpClone = (tmpState.Mode === 'clone');
		tmpState.Step1_CardClass = tmpClone ? 'is-hidden' : '';
		tmpState.Step2_CardClass = tmpClone ? 'is-hidden' : '';
		tmpState.CloneBannerSlot = tmpClone ? [ { Source: tmpState.LakeSourceTable } ] : [];

		// Step-1 source-type tier: Headlight model (entity picker) vs data lake (projection/clone picker).
		const tmpLake = (tmpState.SourceType === 'lake');
		tmpState.SourceTypeEntityChecked = tmpLake ? '' : 'checked';
		tmpState.SourceTypeLakeChecked = tmpLake ? 'checked' : '';
		tmpState.EntityPickerSlot = tmpLake ? [] : [ {} ];
		tmpState.LakePickerSlot = tmpLake ? [ {} ] : [];
		tmpState.TestCountSlot = tmpLake ? [] : [ {} ]; // row-count test is entity-only for now
		(tmpState.LakeSourceGroups || []).forEach((pGroup) =>
		{
			pGroup.Items.forEach((pItem) => { pItem.SelectedAttr = (`lake/${pItem.Physical}` === tmpState.LakeSource) ? 'selected' : ''; });
		});
		tmpState.LakeSourcesEmptySlot = (tmpLake && tmpState.LakeSourcesLoaded
			&& (tmpState.LakeSourcesLoadingSlot || []).length === 0
			&& (tmpState.LakeSourceGroups || []).length === 0) ? [ {} ] : [];
	}

	deriveStepState()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		for (let i = 1; i <= STEP_COUNT; i++)
		{
			tmpState['Step' + i + '_BodyClass'] = (tmpState.ActiveStep === i) ? '' : 'is-hidden';
			if (tmpState.CompletedSteps.includes(i))
			{
				tmpState['Step' + i + '_StatusLabel'] = '✓';
				tmpState['Step' + i + '_StatusClass'] = 'is-success';
			}
			else if (tmpState.ActiveStep === i)
			{
				tmpState['Step' + i + '_StatusLabel'] = 'Active';
				tmpState['Step' + i + '_StatusClass'] = 'is-info';
			}
			else
			{
				tmpState['Step' + i + '_StatusLabel'] = '';
				tmpState['Step' + i + '_StatusClass'] = '';
			}
		}
	}

	deriveFieldState()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		const tmpRows = [];
		let tmpIncluded = 0;
		for (const tmpField of tmpState.Fields)
		{
			if (tmpField.Include) { tmpIncluded++; }
			if (tmpField.IsAudit && !tmpState.ShowAll && !tmpField.Include)
			{
				continue;
			}
			tmpRows.push({
				SourceField: tmpField.SourceField,
				OutCol: tmpField.OutCol,
				JsonType: tmpField.JsonType,
				MeadowType: libBuilder.jsonTypeToMeadow(tmpField.JsonType, tmpField.Size),
				IncludeAttr: tmpField.Include ? 'checked' : '',
				RowClass: tmpField.Include ? '' : 'has-text-grey',
			});
		}
		tmpState.FieldRows = tmpRows;
		const tmpFormDataCount = (tmpState.FormDataFields || []).filter((pField) => pField.Key && String(pField.Key).trim()).length;
		tmpState.IncludedCount = tmpIncluded + tmpFormDataCount;
		tmpState.ShowAllLabel = tmpState.ShowAll ? 'Hide audit columns' : 'Show all columns';
	}

	/** Tier 2: render rows for the manually-added FormData fields (Document source). */
	deriveFormDataRows()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.FormDataRows = (tmpState.FormDataFields || []).map((pField, pIndex) => ({
			Index: pIndex,
			Key: pField.Key,
			OutCol: pField.OutCol,
		}));
		tmpState.FormDataSlot = tmpState.IsFormDataEntity ? [ {} ] : [];
	}

	deriveNameState()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.TargetPreview = (tmpState.IDCustomer && tmpState.Name) ? `lake/C${tmpState.IDCustomer}_${tmpState.Name}` : '';
		tmpState.IDCustomerEditable = this.isSuperUser;
		if (tmpState.Name && !libBuilder.NAME_REGEX.test(tmpState.Name))
		{
			tmpState.NameErrorSlot = [ { Message: 'Letters, numbers and underscores only; must start with a letter.' } ];
		}
		else
		{
			tmpState.NameErrorSlot = [];
		}
	}

	deriveScheduleAttrs()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.ScheduleEnabledAttr = tmpState.ScheduleEnabled ? 'checked' : '';
		tmpState.ScheduleFieldsClass = tmpState.ScheduleEnabled ? '' : 'is-hidden';
		tmpState.ScheduleModeIntervalChecked = (tmpState.ScheduleMode === 'interval') ? 'checked' : '';
		tmpState.ScheduleModeCronChecked = (tmpState.ScheduleMode === 'cron') ? 'checked' : '';
		tmpState.ScheduleIntervalClass = (tmpState.ScheduleMode === 'interval') ? '' : 'is-hidden';
		tmpState.ScheduleCronClass = (tmpState.ScheduleMode === 'cron') ? '' : 'is-hidden';
	}

	// --- Step 1: Source ---

	setEntity(pEntity)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.Entity = pEntity || '';
		const tmpDef = libSourceEntities.DATALAKE_SOURCE_ENTITIES.find((pItem) => pItem.Entity === pEntity);
		tmpState.EntityLabel = tmpDef ? tmpDef.Label : pEntity;
		tmpState.IsFormDataEntity = !!(tmpDef && tmpDef.FormData);
		tmpState.DocumentType = '';
		tmpState.FormDataFields = [];
		tmpState.SourceEntityOptions = tmpState.SourceEntityOptions.map((pOption) => Object.assign({}, pOption, { SelectedAttr: (pOption.Entity === pEntity) ? 'selected' : '' }));
		tmpState.TestResultSlot = [];
		this.render();
	}

	/** Switch the Step-1 source between the Headlight model and the data lake; lazy-load lake sources. */
	setSourceType(pType)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.SourceType = (pType === 'lake') ? 'lake' : 'entity';
		// Switching source kinds invalidates the downstream column choice.
		tmpState.Fields = [];
		tmpState.GUIDTemplate = '';
		tmpState.TestResultSlot = [];
		if (tmpState.SourceType === 'lake' && !tmpState.LakeSourcesLoaded)
		{
			this._loadLakeSourcesAsync();
		}
		this.render();
	}

	/** Resolve a "<kind>::<physical>" option value (from the lake <select>) to a lake source. */
	onLakeSelect(pValue)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (!pValue)
		{
			this.setLakeSource('', '', '');
			return;
		}
		const tmpParts = String(pValue).split('::');
		const tmpKind = tmpParts[0];
		const tmpPhysical = tmpParts.slice(1).join('::');
		let tmpLabel = tmpPhysical;
		(tmpState.LakeSourceGroups || []).forEach((pGroup) => pGroup.Items.forEach((pItem) =>
		{
			if (pItem.Physical === tmpPhysical) { tmpLabel = pItem.Label; }
		}));
		this.setLakeSource(tmpPhysical, tmpKind, tmpLabel);
	}

	/** Pick a data-lake source (an existing projection or imported clone) as the extraction source. */
	setLakeSource(pPhysical, pKind, pLabel)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.LakeSource = pPhysical ? `lake/${pPhysical}` : '';
		tmpState.LakeSourceTable = pPhysical || '';
		tmpState.LakeKind = pKind || '';
		tmpState.LakeSourceLabel = pLabel || pPhysical || '';
		tmpState.LakeSourceValue = pPhysical ? `${pKind || ''}::${pPhysical}` : '';   // picker binding
		tmpState.Fields = [];        // new source → reload its schema in Step 2
		tmpState.GUIDTemplate = '';
		this.render();
	}

	/**
	 * (Re)mount the searchable lake-source picker into #DPE-LakeSourcePicker (replaces the unsearchable grouped
	 * <select>). The lake inventory is already loaded — MaterializedProjections is customer-scoped and DataCatalog is
	 * a single un-paginatable blob — so the DataProvider filters that in-memory merged list and returns GROUPED
	 * results (Computed projections / Imported clones). Called from the view's onAfterRender.
	 */
	mountLakeSourcePicker()
	{
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		if (this.pict.ContentAssignment.getElement('#DPE-LakeSourcePicker').length === 0) { return; }
		const tmpView = tmpPicker.createPicker('DPE-LakeSourcePicker',
			{
				DestinationAddress: '#DPE-LakeSourcePicker',
				ValueAddress: 'AppData.DatalakeProjectionEditor.LakeSourceValue',
				Searchable: true, AllowClear: true, ClearLabel: '— pick a projection or imported clone —',
				Placeholder: 'Search a projection or imported clone…',
				DataProvider: (pTerm) =>
				{
					const tmpTerm = String(pTerm == null ? '' : pTerm).trim().toLowerCase();
					const tmpResults = [];
					(this.pict.AppData.DatalakeProjectionEditor.LakeSourceGroups || []).forEach((pGroup) =>
					{
						(pGroup.Items || []).forEach((pItem) =>
						{
							if (tmpTerm && (String(pItem.Label || '').toLowerCase().indexOf(tmpTerm) < 0) && (String(pItem.Physical || '').toLowerCase().indexOf(tmpTerm) < 0)) { return; }
							tmpResults.push({ Value: `${pItem.Kind}::${pItem.Physical}`, Text: pItem.Label, Group: pGroup.GroupLabel });
						});
					});
					return Promise.resolve({ results: tmpResults, hasMore: false });
				},
				ResolveValue: (pValue) =>
				{
					const tmpValue = String(pValue || '');
					if (!tmpValue) { return Promise.resolve(null); }
					const tmpPhysical = tmpValue.split('::').slice(1).join('::');
					let tmpLabel = tmpPhysical;
					(this.pict.AppData.DatalakeProjectionEditor.LakeSourceGroups || []).forEach((pGroup) => (pGroup.Items || []).forEach((pItem) => { if (pItem.Physical === tmpPhysical) { tmpLabel = pItem.Label; } }));
					return Promise.resolve({ Value: tmpValue, Text: tmpLabel });
				},
				OnChange: (pValue) => { this.onLakeSelect(pValue || ''); },
			});
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
	}

	/** Enumerate the lake's existing computed projections + imported clones as selectable sources. */
	_loadLakeSourcesAsync()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		const tmpCustomerID = parseInt(tmpState.IDCustomer, 10) || 0;
		tmpState.LakeSourcesLoaded = true;
		tmpState.LakeSourcesLoadingSlot = [ {} ];
		this.render();
		const tmpComputed = [];
		const tmpImported = [];
		const tmpMPPhysical = {};

		const fFinish = () =>
		{
			tmpState.LakeSourcesLoadingSlot = [];
			const tmpGroups = [];
			if (tmpComputed.length) { tmpGroups.push({ GroupLabel: 'Computed projections', Items: tmpComputed }); }
			if (tmpImported.length) { tmpGroups.push({ GroupLabel: 'Imported clones', Items: tmpImported }); }
			tmpState.LakeSourceGroups = tmpGroups;
			this.render();
		};

		// 1) Computed projections — the MaterializedProjection registry (customer-scoped).
		const tmpMPURL = 'MaterializedProjections/FilteredTo/FBV~Deleted~EQ~0' + (tmpCustomerID > 0 ? `~FBV~IDCustomer~EQ~${tmpCustomerID}` : '');
		this.restClient.getJSON(tmpMPURL, (pError, pRows) =>
		{
			(Array.isArray(pRows) ? pRows : []).forEach((pRow) =>
			{
				tmpMPPhysical[pRow.PhysicalTableName] = true;
				if (libCatalog.classifyKind(pRow) === 'computed')
				{
					tmpComputed.push({ Physical: pRow.PhysicalTableName, Label: pRow.ProjectionName || libCatalog.deriveLogicalReadName(pRow.PhysicalTableName), Kind: 'computed' });
				}
			});
			// 2) Imported clones — the DataCatalog lake inventory minus the registered (MP) physical names.
			this.restClient.getJSON('DataCatalog', (pCatErr, pCatalog) =>
			{
				const tmpLake = (pCatalog && pCatalog.Sources && pCatalog.Sources.lake) || {};
				const tmpDatasets = tmpLake.Datasets || tmpLake.datasets || [];
				const tmpPrefix = `C${tmpCustomerID}_`;
				(Array.isArray(tmpDatasets) ? tmpDatasets : Object.keys(tmpDatasets))
					.map((pDataset) => (typeof pDataset === 'string') ? pDataset : (pDataset.Name || pDataset.Address || ''))
					.map((pString) => String(pString).replace(/^lake\//, ''))
					.filter((pName) => pName.startsWith(tmpPrefix) && !tmpMPPhysical[pName])
					.forEach((pName) => { tmpImported.push({ Physical: pName, Label: libCatalog.deriveLogicalReadName(pName), Kind: 'imported' }); });
				fFinish();
			});
		});
	}

	/** Tier 2: pick a DocumentType — auto-fills the filter to scope Documents to that type. */
	setDocumentType(pValue)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.DocumentType = pValue || '';
		tmpState.Filter = tmpState.DocumentType
			? `FBV~DocumentType~EQ~${tmpState.DocumentType}~FBV~Deleted~EQ~0`
			: '';
		this.render();
	}

	/** Tier 2: add an empty FormData.<key> column for the user to fill in. */
	addFormDataField()
	{
		this.pict.AppData.DatalakeProjectionEditor.FormDataFields.push({ Key: '', OutCol: '' });
		this.render();
	}

	setFormDataKey(pIndex, pValue)
	{
		const tmpField = this.pict.AppData.DatalakeProjectionEditor.FormDataFields[pIndex];
		if (tmpField) { tmpField.Key = pValue || ''; }
	}

	setFormDataOutCol(pIndex, pValue)
	{
		const tmpField = this.pict.AppData.DatalakeProjectionEditor.FormDataFields[pIndex];
		if (tmpField) { tmpField.OutCol = pValue || ''; }
	}

	removeFormDataField(pIndex)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.FormDataFields.splice(pIndex, 1);
		this.render();
	}

	setFilter(pValue)
	{
		this.pict.AppData.DatalakeProjectionEditor.Filter = pValue || '';
	}

	/** Count the source rows for the current entity + filter, so a zero-row source is caught up front. */
	testSource()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (!tmpState.Entity || tmpState.TestBusy)
		{
			return;
		}
		tmpState.TestBusy = true;
		tmpState.TestResultSlot = [ { Class: 'is-info', Message: 'Counting source rows…' } ];
		this.render();
		const tmpFilter = (tmpState.Filter || '').trim();
		const tmpURL = tmpFilter ? `${tmpState.Entity}s/Count/FilteredTo/${tmpFilter}` : `${tmpState.Entity}s/Count`;
		this.restClient.getJSON(tmpURL, (pError, pBody) =>
		{
			tmpState.TestBusy = false;
			if (pError)
			{
				tmpState.TestResultSlot = [ { Class: 'is-danger', Message: `Could not count the source: ${pError.message || pError}` } ];
			}
			else
			{
				const tmpCount = (pBody && (typeof(pBody.Count) === 'number')) ? pBody.Count : (parseInt(pBody && pBody.Count, 10) || 0);
				tmpState.TestResultSlot = (tmpCount > 0)
					? [ { Class: 'is-success', Message: `${tmpCount.toLocaleString('en-US')} source row(s) match.` } ]
					: [ { Class: 'is-warning', Message: 'No source rows match — the projection would be empty. Check the entity / filter.' } ];
			}
			this.render();
		});
	}

	/** Validate the source choice, load the source schema, advance to Step 2. */
	continueFromSource()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (tmpState.SourceType === 'lake')
		{
			if (!tmpState.LakeSource)
			{
				tmpState.TestResultSlot = [ { Class: 'is-danger', Message: 'Pick a data-lake source to continue.' } ];
				this.render();
				return;
			}
			this._loadLakeSchema(tmpState.LakeSourceTable, tmpState.LakeKind);
			return;
		}
		if (!tmpState.Entity)
		{
			tmpState.TestResultSlot = [ { Class: 'is-danger', Message: 'Pick a source entity to continue.' } ];
			this.render();
			return;
		}
		this._loadEntitySchema(tmpState.Entity);
	}

	_loadEntitySchema(pEntity)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.SchemaErrorSlot = [];
		tmpState.SchemaLoadingSlot = [ {} ];
		this._completeStep(1); // advance now; the field list fills in when the schema resolves

		const fApply = (pProperties) =>
		{
			const tmpDef = libSourceEntities.DATALAKE_SOURCE_ENTITIES.find((pItem) => pItem.Entity === pEntity);
			const tmpIDField = (tmpDef && tmpDef.DefaultIdentifier) || `ID${pEntity}`;
			this._applySchema(pProperties, `${pEntity}_{~D:Record.${tmpIDField}~}`, false);
		};

		if (this._schemaCache[pEntity])
		{
			fApply(this._schemaCache[pEntity]);
			return;
		}
		this.restClient.getJSON(`${pEntity}/Schema`, (pError, pBody) =>
		{
			if (pError || !pBody || !pBody.properties)
			{
				tmpState.SchemaLoadingSlot = [];
				tmpState.SchemaErrorSlot = [ { Message: `Could not load the ${pEntity} schema: ${pError ? (pError.message || pError) : 'no schema returned'}.` } ];
				this.render();
				return;
			}
			this._schemaCache[pEntity] = pBody.properties;
			fApply(pBody.properties);
		});
	}

	/**
	 * Build the Step-2 field model from a { properties: { col: { type, size } } } schema (entity or lake
	 * source). For lake sources only the standard audit columns are pre-excluded (no entity-named GUID).
	 * @param {object} pProperties
	 * @param {string} pGuidDefault
	 * @param {boolean} pIsLake
	 */
	_applySchema(pProperties, pGuidDefault, pIsLake)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.SchemaLoadingSlot = [];
		tmpState.Fields = Object.keys(pProperties).map((pKey) =>
		{
			const tmpAudit = pIsLake ? !!AUDIT_FIELDS[pKey] : isAuditField(pKey, tmpState.Entity);
			return {
				SourceField: pKey,
				OutCol: pKey,
				JsonType: (pProperties[pKey] && pProperties[pKey].type) || 'string',
				Size: (pProperties[pKey] && pProperties[pKey].size) || '',
				IsAudit: tmpAudit,
				Include: !tmpAudit,
			};
		});
		if (!tmpState.GUIDTemplate) { tmpState.GUIDTemplate = pGuidDefault; }
		this.render();
	}

	/**
	 * Load a lake source's columns + advance to Step 2: a computed projection via PrivateDataLake/Schema
	 * (REST-readable, works today), an imported clone via /mapper/introspect (deploy-gated — graceful
	 * message until the mapper service is live). Both flow through _applySchema like an entity source.
	 * @param {string} pPhysical the C<id>_<name> physical table
	 * @param {string} pKind 'computed' | 'imported'
	 */
	_loadLakeSchema(pPhysical, pKind)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.SchemaErrorSlot = [];
		tmpState.SchemaLoadingSlot = [ {} ];
		this._completeStep(1);
		const tmpLogical = String(pPhysical).replace(/^C\d+_/, '');
		const fGuidDefault = (pCols) => (pCols.indexOf('GUID') >= 0)
			? '{~D:Record.GUID~}'
			: `${tmpLogical}_{~D:Record.${pCols.find((pCol) => /^ID/i.test(pCol)) || pCols[0] || 'GUID'}~}`;

		if (pKind === 'imported')
		{
			// Raw clone columns are hidden from the meadow API — introspect via the mapper service.
			this.restClient.getJSON(`mapper/introspect?address=lake/${pPhysical}`, (pError, pBody) =>
			{
				if (pError || !pBody || !Array.isArray(pBody.Columns))
				{
					tmpState.SchemaLoadingSlot = [];
					tmpState.SchemaErrorSlot = [ { Message: 'Imported-clone schemas are read through the data-mapper service, which isn’t available here yet. Once it’s deployed the columns will load automatically — computed projections work today.' } ];
					this.render();
					return;
				}
				const tmpProps = {};
				pBody.Columns.forEach((pColumn) =>
				{
					const tmpName = pColumn.Name || pColumn.Column;
					if (tmpName) { tmpProps[tmpName] = { type: libBuilder.dbTypeToJsonType(pColumn.Type), size: pColumn.Size || '' }; }
				});
				this._applySchema(tmpProps, fGuidDefault(Object.keys(tmpProps)), true);
			});
			return;
		}
		// Computed projection — REST-readable JSON-Schema, same shape as an entity schema.
		this.restClient.getJSON(`PrivateDataLake/${tmpLogical}/Schema`, (pError, pBody) =>
		{
			if (pError || !pBody || !pBody.properties)
			{
				tmpState.SchemaLoadingSlot = [];
				tmpState.SchemaErrorSlot = [ { Message: `Could not load the source projection schema: ${pError ? (pError.message || pError) : 'no schema returned'}.` } ];
				this.render();
				return;
			}
			this._applySchema(pBody.properties, fGuidDefault(Object.keys(pBody.properties)), true);
		});
	}

	// --- Step 2: Columns + GUID ---

	toggleField(pSourceField)
	{
		const tmpField = this.pict.AppData.DatalakeProjectionEditor.Fields.find((pItem) => pItem.SourceField === pSourceField);
		if (tmpField) { tmpField.Include = !tmpField.Include; }
		this.render();
	}

	setOutCol(pSourceField, pValue)
	{
		const tmpField = this.pict.AppData.DatalakeProjectionEditor.Fields.find((pItem) => pItem.SourceField === pSourceField);
		if (tmpField) { tmpField.OutCol = (pValue || '').trim() || pSourceField; }
		// no render — avoid resetting the text cursor
	}

	toggleShowAll()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		tmpState.ShowAll = !tmpState.ShowAll;
		this.render();
	}

	setGUIDTemplate(pValue)
	{
		this.pict.AppData.DatalakeProjectionEditor.GUIDTemplate = pValue || '';
	}

	continueFromColumns()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		const tmpIncluded = this._buildState().fields.filter((pField) => pField.Include !== false);
		if (tmpIncluded.length < 1)
		{
			tmpState.SchemaErrorSlot = [ { Message: 'Select at least one column to project (or add a form field).' } ];
			this.render();
			return;
		}
		if (!tmpState.GUIDTemplate || tmpState.GUIDTemplate.indexOf('{~D:Record.') < 0)
		{
			tmpState.SchemaErrorSlot = [ { Message: 'The GUID template must reference a record field, e.g. {~D:Record.IDProject~}.' } ];
			this.render();
			return;
		}
		tmpState.SchemaErrorSlot = [];
		this._completeStep(2);
	}

	// --- Step 3: Name / Target / Schedule ---

	setName(pValue)
	{
		this.pict.AppData.DatalakeProjectionEditor.Name = (pValue || '').trim();
		this.deriveNameState();
		// Re-render to update the Target preview + name error (the name input keeps focus via onchange).
		this.render();
	}

	setDescription(pValue)
	{
		this.pict.AppData.DatalakeProjectionEditor.Description = pValue || '';
	}

	setCustomer(pValue)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (!this.isSuperUser) { return; }
		tmpState.IDCustomer = parseInt(pValue, 10) || 0;
		this.render();
	}

	setScheduleEnabled(pChecked)
	{
		this.pict.AppData.DatalakeProjectionEditor.ScheduleEnabled = !!pChecked;
		this.deriveScheduleAttrs();
		this.render();
	}

	setScheduleMode(pMode)
	{
		this.pict.AppData.DatalakeProjectionEditor.ScheduleMode = (pMode === 'cron') ? 'cron' : 'interval';
		this.deriveScheduleAttrs();
		this.render();
	}

	setScheduleField(pField, pValue)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (pField === 'cron') { tmpState.Cron = pValue || ''; }
		else if (pField === 'interval') { tmpState.IntervalMinutes = parseInt(pValue, 10) || 0; }
		else if (pField === 'group') { tmpState.Group = pValue || ''; }
	}

	continueFromName()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		const tmpBuildState = this._buildState();
		const tmpValidation = (tmpState.Mode === 'clone')
			? libBuilder.validateCloneState(tmpBuildState)
			: libBuilder.validateExtractionState(tmpBuildState);
		if (!tmpValidation.Valid)
		{
			tmpState.NameErrorSlot = [ { Message: tmpValidation.Errors[0] } ];
			this.render();
			return;
		}
		// Build the review body and advance.
		tmpState.ReviewJSON = JSON.stringify(
			(tmpState.Mode === 'clone') ? libBuilder.buildPassthroughCloneBody(tmpBuildState) : libBuilder.buildExtractionBody(tmpBuildState),
			null, 2);
		this._completeStep(3);
	}

	// --- Step 4: Review ---

	backToStep(pStep)
	{
		this.goToStep(pStep);
	}

	confirmReview()
	{
		this._completeStep(4);
	}

	// --- Step 5: Submit ---

	_buildState()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		// Tier 2: merge the manually-added FormData.<key> columns (all Text) into the field set.
		const tmpFormDataFields = (tmpState.FormDataFields || [])
			.filter((pField) => pField.Key && String(pField.Key).trim())
			.map((pField) =>
			{
				const tmpKey = String(pField.Key).trim();
				return {
					SourceField: `FormData.${tmpKey}`,
					OutCol: (pField.OutCol && pField.OutCol.trim()) || tmpKey.replace(/[^A-Za-z0-9_]/g, '_'),
					JsonType: 'string',
					Size: 'Default',
					Include: true,
				};
			});
		return {
			IDCustomer: tmpState.IDCustomer,
			Name: tmpState.Name,
			SourceType: tmpState.SourceType,
			Entity: tmpState.Entity,
			SourceConnectionPrefix: tmpState.SourceConnectionPrefix,
			Filter: tmpState.Filter,
			GUIDTemplate: tmpState.GUIDTemplate,
			Description: tmpState.Description,
			fields: tmpState.Fields.concat(tmpFormDataFields),
			Mode: tmpState.Mode,
			LakeSource: tmpState.LakeSource,
		};
	}

	submit()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (tmpState.Submitting)
		{
			return;
		}
		const tmpBuildState = this._buildState();
		const tmpValidation = (tmpState.Mode === 'clone')
			? libBuilder.validateCloneState(tmpBuildState)
			: libBuilder.validateExtractionState(tmpBuildState);
		if (!tmpValidation.Valid)
		{
			tmpState.ResultSlot = [ { Class: 'is-danger', Icon: 'fa-triangle-exclamation', Title: 'Cannot submit', Detail: tmpValidation.Errors.join(' ') } ];
			this.render();
			return;
		}
		const tmpBody = (tmpState.Mode === 'clone')
			? libBuilder.buildPassthroughCloneBody(tmpBuildState)
			: libBuilder.buildExtractionBody(tmpBuildState);
		tmpState.Submitting = true;
		tmpState.SubmitBusySlot = [ {} ];
		tmpState.ResultSlot = [ { Class: 'is-info', Icon: 'fa-rotate', Title: 'Creating projection…', Detail: 'Running the extraction — this can take a while for large sources.' } ];
		this.render();

		this.restClient.postJSON('Projection', tmpBody, (pError, pResponse) =>
		{
			tmpState.Submitting = false;
			tmpState.SubmitBusySlot = [];
			if (pError)
			{
				tmpState.ResultSlot = [ { Class: 'is-warning', Icon: 'fa-clock', Title: 'Submitted, not confirmed', Detail: `The projection was submitted but didn't confirm in time — it may still be running. Check the catalog shortly. (${pError.message || pError})` } ];
				this.render();
				return;
			}
			this._handleRunResult(pResponse);
		});
	}

	_handleRunResult(pResponse)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		const tmpOK = pResponse && (pResponse.Success === true || /complete/i.test(String(pResponse.Status || '')));
		const tmpRead = (pResponse && pResponse.RowsRead != null) ? pResponse.RowsRead : '?';
		const tmpWritten = (pResponse && pResponse.RowsWritten != null) ? pResponse.RowsWritten : '?';
		if (!tmpOK)
		{
			const tmpFailure = pResponse && pResponse.Failure;
			tmpState.ResultSlot = [ { Class: 'is-danger', Icon: 'fa-triangle-exclamation', Title: 'Projection failed', Detail: (tmpFailure && tmpFailure.Reason) ? tmpFailure.Reason : 'The extraction did not complete successfully.' } ];
			this.render();
			return;
		}
		const tmpDetail = (tmpWritten === 0)
			? `Completed, but wrote 0 rows — check the source filter. (${tmpRead} read)`
			: `${tmpRead} read · ${tmpWritten} written.`;
		tmpState.ResultSlot = [ { Class: (tmpWritten === 0) ? 'is-warning' : 'is-success', Icon: (tmpWritten === 0) ? 'fa-circle-exclamation' : 'fa-circle-check', Title: 'Projection created', Detail: tmpDetail } ];
		this._completeStep(5);
		this.render();
		// Apply the schedule via a follow-up PUT (POST /1.0/Projection takes no schedule fields).
		if (tmpState.ScheduleEnabled)
		{
			this._applySchedule();
		}
	}

	/** Resolve the new registry row by (IDCustomer, ProjectionName) and PUT the schedule triplet. */
	_applySchedule()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		this.restClient.getJSON(`MaterializedProjections/FilteredTo/FBV~IDCustomer~EQ~${tmpState.IDCustomer}~FBV~ProjectionName~EQ~${encodeURIComponent(tmpState.Name)}/0/1`, (pError, pBody) =>
		{
			if (pError || !Array.isArray(pBody) || pBody.length < 1)
			{
				return;
			}
			tmpState.NewDatasetGUID = pBody[0].GUIDMaterializedProjection;
			const tmpRecord =
			{
				IDMaterializedProjection: pBody[0].IDMaterializedProjection,
				RefreshEnabled: 1,
				RefreshCron: (tmpState.ScheduleMode === 'cron') ? String(tmpState.Cron || '') : '',
				RefreshIntervalMinutes: (tmpState.ScheduleMode === 'interval') ? (parseInt(tmpState.IntervalMinutes, 10) || 0) : 0,
				RefreshGroup: String(tmpState.Group || ''),
			};
			this.restClient.updateEntity('MaterializedProjection', tmpRecord, () => { /* best-effort; the result banner already shows success */ });
		});
	}

	viewInCatalog()
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (tmpState.NewDatasetGUID)
		{
			window.location.hash = `#/Datalake/Dataset/${tmpState.NewDatasetGUID}`;
		}
		else
		{
			window.location.hash = '#/Datalake';
		}
	}

	// --- step navigation ---

	goToStep(pStep)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (pStep !== tmpState.ActiveStep && !tmpState.CompletedSteps.includes(pStep) && pStep > tmpState.ActiveStep)
		{
			return;
		}
		tmpState.ActiveStep = pStep;
		this.render();
	}

	_completeStep(pStep)
	{
		const tmpState = this.pict.AppData.DatalakeProjectionEditor;
		if (!tmpState.CompletedSteps.includes(pStep))
		{
			tmpState.CompletedSteps.push(pStep);
		}
		tmpState.ActiveStep = Math.min(pStep + 1, STEP_COUNT);
		this.render();
	}
}

module.exports = DatalakeProjectionEditorProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'DatalakeProjectionEditorProvider',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
};

module.exports.isAuditField = isAuditField;
